import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { db } from '../../services/db';
import Button from '../../components/common/Button';
import SafeIcon from '../../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const AboutEditor = () => {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    const data = await db.getAboutContent();
    setContent(data);
  };

  const handleChange = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleChange('photo', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const newLogos = [...(content.mediaLogos || [])];
        newLogos[index] = { image: reader.result, alt: newLogos[index]?.alt || '' };
        handleChange('mediaLogos', newLogos);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleLogoAltText = (index, alt) => {
    const newLogos = [...(content.mediaLogos || [])];
    newLogos[index] = { ...newLogos[index], alt };
    handleChange('mediaLogos', newLogos);
  };

  const addLogoSlot = () => {
    const newLogos = [...(content.mediaLogos || []), { image: '', alt: '' }];
    handleChange('mediaLogos', newLogos);
  };

  const removeLogoSlot = (index) => {
    const newLogos = content.mediaLogos.filter((_, i) => i !== index);
    handleChange('mediaLogos', newLogos);
  };

  const handleSave = async () => {
    setSaving(true);
    await db.updateAboutContent(content);
    setSaving(false);
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  };

  if (!content) return <AdminLayout><div>Loading...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="max-w-4xl space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
          {/* Photo Upload */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Jenny's Photo
            </label>
            {content.photo && (
              <div className="mb-4 flex items-center gap-4">
                <img
                  src={content.photo}
                  alt="Jenny"
                  className="w-32 h-32 rounded-full object-cover border-4 border-pink-200"
                />
                <button
                  onClick={() => handleChange('photo', '')}
                  className="text-red-600 hover:text-red-700 text-sm font-medium"
                >
                  Remove Photo
                </button>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg"
            />
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Title
            </label>
            <input
              type="text"
              value={content.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none"
            />
          </div>

          {/* Mission Quote Spanish */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Mission Quote (Spanish)
            </label>
            <textarea
              value={content.quoteEs}
              onChange={(e) => handleChange('quoteEs', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none"
            />
          </div>

          {/* Mission Quote English */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Mission Quote (English)
            </label>
            <textarea
              value={content.quoteEn}
              onChange={(e) => handleChange('quoteEn', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none"
            />
          </div>

          {/* Bio Spanish */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Full Bio (Spanish)
            </label>
            <textarea
              value={content.bioEs}
              onChange={(e) => handleChange('bioEs', e.target.value)}
              rows={5}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none"
            />
          </div>

          {/* Bio English */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Full Bio (English)
            </label>
            <textarea
              value={content.bioEn}
              onChange={(e) => handleChange('bioEn', e.target.value)}
              rows={5}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 outline-none"
            />
          </div>

          {/* Media Logos */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Media Logos (Featured In)
            </label>
            <div className="space-y-4">
              {(content.mediaLogos || []).map((logo, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Logo {index + 1}</span>
                    <button
                      onClick={() => removeLogoSlot(index)}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      <SafeIcon icon={FiIcons.FiTrash2} />
                    </button>
                  </div>
                  {logo.image && (
                    <img src={logo.image} alt={logo.alt} className="h-12 object-contain" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleLogoUpload(e, index)}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                  <input
                    type="text"
                    value={logo.alt}
                    onChange={(e) => handleLogoAltText(index, e.target.value)}
                    placeholder="Alt text (e.g., Vogue Logo)"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  />
                </div>
              ))}
              {(content.mediaLogos || []).length < 4 && (
                <button
                  onClick={addLogoSlot}
                  className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-pink-500 hover:text-pink-600 transition-colors"
                >
                  + Add Logo
                </button>
              )}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex items-center gap-4 pt-4 border-t">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
            {success && (
              <div className="flex items-center gap-2 text-green-600">
                <SafeIcon icon={FiIcons.FiCheck} />
                <span className="text-sm font-medium">Saved successfully!</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AboutEditor;